import { createAgenda, isElectron, updateMeeting } from '@groupthinkai/groupthink';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import * as React from 'react';

import useDataLayer from '@/hooks/dataLayer';
import { useCurrentOrganization } from '@/hooks/organization';

export function DesktopEventHandler({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [currentOrganization] = useCurrentOrganization();
  const [isCreating, setIsCreating] = React.useState(false);
  const { pushData } = useDataLayer();

  // Use ref to store the latest handler without triggering re-renders
  const handleNewNotesRef = React.useRef<() => Promise<void>>();
  const handleEndMeetingFromDesktopTrayRef =
    React.useRef<(agendaId: string, meetingId: string) => Promise<void>>();

  handleNewNotesRef.current = async () => {
    if (isCreating) {
      return;
    }

    pushData('Tray Item Clicked', {
      workspace_id: currentOrganization,
      button_name: 'Start New Note',
      button_intent: 'create note',
      button_context: 'new notes creation',
    });

    await createAgenda({
      payload: {
        name: 'Meeting Notes ' + dayjs().format('hh:mm A on M/D/YYYY'),
        workspace_id: currentOrganization,
        visibility: 'protected',
        // @ts-ignore
        private: false,
      },
      setIsCreating,
      onSuccess: (agenda) => {
        router.push(`/agendas/${agenda.id}`);
      },
    });
  };

  handleEndMeetingFromDesktopTrayRef.current = async (agendaId: string, meetingId: string) => {
    updateMeeting(agendaId, meetingId, {
      // @ts-ignore
      payload: { end: true },
    });
  };

  const stableHandleNewNotes = React.useCallback(() => {
    return handleNewNotesRef.current?.();
  }, []);
  const stableHandleEndMeetingFromDesktopTray = React.useCallback(
    (data: { agendaId: string; meetingId: string }) => {
      const { agendaId, meetingId } = data;
      return handleEndMeetingFromDesktopTrayRef.current?.(agendaId, meetingId);
    },
    []
  );

  React.useEffect(() => {
    if (!isElectron) {
      return;
    }

    const cleanupCreateNewNote = window.electron?.onCreateNewNote?.(stableHandleNewNotes);
    const cleanupStopTakingNotes = window.electron?.onStopTakingNotes?.(
      stableHandleEndMeetingFromDesktopTray
    );

    return () => {
      if (cleanupCreateNewNote) cleanupCreateNewNote();
      if (cleanupStopTakingNotes) cleanupStopTakingNotes();
    };
  }, []);

  return <>{children}</>;
}
